import { useState, useEffect } from "react";
import Linkify from "../components/Linkify";
import { addMemo, deleteMemo, addReply, deleteReply, toggleLike } from "../api/memos";
import { sendNotification } from "../api/users";

const BookDiscussion = ({ book, currentUser, users, onMemoAdded, focusId, clearFocus }) => {
  /** 방금 데려온 글. 잠깐 테두리를 밝혀 어느 글인지 알게 한다. */
  const [flash, setFlash] = useState(null);

  /* 이달의 이야기에서 눌러 들어오면 그 글 자리로 데려간다.
     탭이 막 바뀐 참이라 글이 아직 그려지지 않았을 수 있어, 나타날 때까지
     몇 프레임 찾는다. 찾으면 곧바로 focusId 를 비운다 — 남겨두면 이 책을
     다시 열 때마다 같은 자리로 끌려간다. */
  useEffect(() => {
    /* "q:" 로 시작하면 문장을 짚어 달라는 뜻이다. 여기서 받아 clearFocus 를
       부르면 문장 쪽이 받기도 전에 지워진다. */
    if (!focusId || String(focusId).startsWith("q:")) return;
    let raf = 0, stopped = false;
    const deadline = performance.now() + 1500;
    const tick = () => {
      if (stopped) return;
      const el = document.querySelector(`[data-memo-id="${CSS.escape(String(focusId))}"]`);
      if (el) {
        setFlash(focusId);
        clearFocus?.();
        return;
      }
      if (performance.now() < deadline) raf = requestAnimationFrame(tick);
      else clearFocus?.();
    };
    raf = requestAnimationFrame(tick);
    return () => { stopped = true; cancelAnimationFrame(raf); };
  }, [focusId, clearFocus]);

  /* 스크롤과 밝힘은 flash 에 묶는다. focusId 에 묶으면 clearFocus() 가
     곧바로 이펙트를 다시 돌려 예약해 둔 일을 취소해버린다 — 월별 탭
     스크롤에서 한 번 겪은 일이다.
     글이 화면보다 짧으면 가운데로, 길면 머리를 back-bar 아래에 둔다.
     위쪽 표지가 늦게 자리를 잡으면 높이가 바뀌므로 한 번 더 맞춘다. */
  useEffect(() => {
    if (!flash) return;
    const place = () => {
      const el = document.querySelector(`[data-memo-id="${CSS.escape(String(flash))}"]`);
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const h = el.offsetHeight;
      const y = h < window.innerHeight * 0.7
        ? window.scrollY + top - (window.innerHeight - h) / 2
        : window.scrollY + top - 90;
      window.scrollTo(0, Math.max(0, y));
    };
    place();
    const again = setTimeout(place, 320);
    const off = setTimeout(() => setFlash(null), 2400);
    return () => { clearTimeout(again); clearTimeout(off); };
  }, [flash]);
  const [discussionText, setDiscussionText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const uid = currentUser?.uid;
  const publicMemos = (book.memos || []).filter(m => m.isPublic && m.userId !== uid);
  const myPublic = (book.memos || []).filter(m => m.isPublic && m.userId === uid);
  // createdAtRaw 는 ISO 타임스탬프라 같은 날 안에서도 순서가 유지된다
  const stamp = (m) => m.createdAtRaw || m.createdAt || "";
  const allPublic = [...myPublic, ...publicMemos].sort((a, b) => stamp(b).localeCompare(stamp(a)));
  const getUser = (id) => (users || []).find(u => u.id === id) || { name: "독자", avatar: "📖" };

  /*
   * 여기 있던 네 개의 핸들러는 전부 같은 문제를 갖고 있었다:
   * books 문서를 getDoc 으로 읽고 → memos 배열을 map 으로 갈아끼우고 → 통째로 updateDoc.
   * 두 사람이 동시에 좋아요나 댓글을 남기면 나중 쓰기가 앞선 것을 덮어썼다.
   * 이제 메모·댓글·좋아요가 각각 독립 행이라 서로 충돌하지 않는다.
   */

  const handleLike = async (memo) => {
    if (!uid) return;
    const liked = (memo.likes || []).includes(uid);
    await toggleLike(memo.id, uid, liked);
    /* 누를 때만 알린다. 취소는 알릴 일이 아니다.
       알림이 실패해도 좋아요 자체는 이미 눌렸다 — 여기서 던지면
       화면이 갱신되지 않아 눌리지 않은 것처럼 보인다. */
    if (!liked && memo.userId !== uid) {
      try {
        await sendNotification({
          toUserId: memo.userId, fromUserId: uid,
          type: "memo_like", bookTitle: book.title,
        });
      } catch (e) {
        console.error("좋아요 알림 실패:", e.message);
      }
    }
    if (onMemoAdded) onMemoAdded(book.id, null);
  };

  const handleDeletePublicMemo = async (memo) => {
    if (!window.confirm("이 게시글을 삭제할까요?")) return;
    await deleteMemo(memo.id);
    if (onMemoAdded) onMemoAdded(book.id, null);
  };

  const handleDeleteReply = async (memo, reply) => {
    if (!window.confirm("이 댓글을 삭제할까요?")) return;
    await deleteReply(reply.id);
    if (onMemoAdded) onMemoAdded(book.id, null);
  };

  const handleAddReply = async (memo) => {
    if (!uid || !replyText.trim()) return;
    await addReply({ memoId: memo.id, userId: uid, text: replyText });
    setReplyText("");
    setReplyingTo(null);
    /* 글쓴이에게만 알린다. 같은 글에 댓글을 단 사람들까지 부르면 한 줄이
       오갈 때마다 여러 명에게 울린다.
       좋아요와 같은 이유로 던지지 않는다 — 댓글은 이미 달렸다. */
    if (memo.userId !== uid) {
      try {
        await sendNotification({
          toUserId: memo.userId, fromUserId: uid,
          type: "reply", bookTitle: book.title,
        });
      } catch (e) {
        console.error("댓글 알림 실패:", e.message);
      }
    }
    if (onMemoAdded) onMemoAdded(book.id, null);
  };

  const handlePostDiscussion = async () => {
    if (!uid || !discussionText.trim()) return;
    await addMemo({ bookId: book.id, userId: uid, text: discussionText, isPublic: true });
    setDiscussionText("");
    if (onMemoAdded) onMemoAdded(book.id, null);
  };

  return (
    <div className="memo-wrap">
      {!currentUser?.isAnonymous && (
        <div className="memo-compose memo-compose--discussion">
          <textarea
            className="memo-textarea"
            placeholder="이 책에 대해 이야기해보세요... 질문, 감상, 궁금한 점 무엇이든!"
            value={discussionText}
            onChange={e => setDiscussionText(e.target.value)}
            rows={3}
          />
          <div className="memo-compose-actions">
            <button className="memo-save-btn" onClick={handlePostDiscussion} disabled={!discussionText.trim()}>등록</button>
          </div>
        </div>
      )}
      {allPublic.length === 0 ? (
        <div className="memo-empty">아직 이야기가 없어요. 첫 이야기를 시작해보세요!</div>
      ) : (
        allPublic.map(m => {
          const u = m.userId === uid ? { name: "나", avatar: "🌿" } : getUser(m.userId);
          const likes = m.likes || [];
          const hasLiked = likes.includes(uid);
          const replies = m.replies || [];
          const isReplying = replyingTo === m.id;
          return (
            <div key={m.id} data-memo-id={m.id} className={`memo-item${flash === m.id ? " memo-focus" : ""}`}>
              <div className="memo-item-header">
                <span className="memo-item-avatar">{u.avatar}</span>
                <span className="memo-item-name">{u.name}</span>
                <span className="memo-item-date">{m.createdAt}</span>
                {m.userId === uid && (
                  <button className="memo-item-delete" onClick={() => handleDeletePublicMemo(m)}>✕</button>
                )}
              </div>
              <div className="memo-item-text"><Linkify text={m.text} /></div>
              <div className="memo-actions">
                <button className={`memo-action-btn${hasLiked ? " liked" : ""}`} onClick={() => handleLike(m)}>
                  {hasLiked ? "❤️" : "🤍"} {likes.length > 0 && <span>{likes.length}</span>}
                </button>
                <button className="memo-action-btn" onClick={() => { setReplyingTo(isReplying ? null : m.id); setReplyText(""); }}>
                  💬 {replies.length > 0 && <span>{replies.length}</span>}
                </button>
              </div>
              {replies.length > 0 && (
                <div className="memo-replies">
                  {replies.map(r => {
                    const ru = r.userId === uid ? { name: "나", avatar: "🌿" } : getUser(r.userId);
                    return (
                      <div key={r.id} className="memo-reply">
                        <span className="memo-reply-avatar">{ru.avatar}</span>
                        <div className="memo-reply-body">
                          <span className="memo-reply-name">{ru.name}</span>
                          <span className="memo-reply-text"><Linkify text={r.text} /></span>
                          <span className="memo-reply-date">{r.createdAt}</span>
                        </div>
                        {r.userId === uid && (
                          <button className="memo-reply-delete" onClick={() => handleDeleteReply(m, r)}>✕</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {isReplying && !currentUser?.isAnonymous && (
                <div className="memo-reply-input">
                  <input
                    type="text"
                    placeholder="댓글을 남겨보세요..."
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && replyText.trim()) handleAddReply(m); }}
                    autoFocus
                  />
                  <button onClick={() => handleAddReply(m)} disabled={!replyText.trim()}>등록</button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default BookDiscussion;
